import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Bell, Download, QrCode, Share2, Tv } from 'lucide-react';
import { api } from '@/api';
import { formatDate, formatTime } from '@/utils/helpers';

const AdminDashboard = ({ stats, sports, leagues, onRefresh }) => {
  const [seeding, setSeeding] = useState(false);
  const [upcomingStreams, setUpcomingStreams] = useState([]);
  const [loadingStreams, setLoadingStreams] = useState(true);
  const [generatingPromo, setGeneratingPromo] = useState(null);
  const [generatedPromo, setGeneratedPromo] = useState(null);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);

  useEffect(() => {
    loadUpcomingStreams();
  }, []);

  const loadUpcomingStreams = async () => {
    try {
      const res = await api.getUpcomingStreams();
      setUpcomingStreams(res.data.games || []);
    } catch (error) {
      console.error('Error loading upcoming streams:', error);
    } finally {
      setLoadingStreams(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await api.seedDatabase();
      await onRefresh();
      alert('Database seeded successfully!');
    } catch (error) {
      alert('Error seeding database');
    } finally {
      setSeeding(false);
    }
  };

  const handleGeneratePromo = async (game, formatType) => {
    setGeneratingPromo(game.id);
    try {
      const watchUrl = game.video_sources?.[0]?.url || 'https://sports.newlogical.com';
      const res = await api.generatePromoGraphic(game.id, watchUrl, formatType);
      setGeneratedPromo(res.data);
      setSelectedGame(game);
      setShowPromoModal(true);
    } catch (error) {
      alert('Error generating promo graphic');
      console.error(error);
    } finally {
      setGeneratingPromo(null);
    }
  };

  const downloadPromo = () => {
    if (generatedPromo) {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${generatedPromo.base64}`;
      link.download = generatedPromo.filename;
      link.click();
    }
  };

  const shareToInstagram = () => {
    downloadPromo();
    setTimeout(() => {
      window.open('instagram://camera', '_blank');
    }, 500);
  };

  return (
    <div className="space-y-6">
      {/* Upcoming Streams Notification */}
      {upcomingStreams.length > 0 && (
        <Card className="bg-gradient-to-r from-sky-900/50 to-purple-900/50 border-sky-400">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center">
              <Bell className="w-5 h-5 mr-2 text-yellow-400 animate-pulse" />
              Upcoming Streams - Promo Graphics Ready!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sky-200 mb-4">
              You have {upcomingStreams.length} stream{upcomingStreams.length > 1 ? 's' : ''} within the next 24 hours. 
              Generate promotional graphics for Instagram!
            </p>
            <div className="space-y-3">
              {upcomingStreams.map(game => (
                <div key={game.id} className="flex items-center justify-between p-4 bg-gray-800/70 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="bg-sky-500/20 p-2 rounded-full">
                      <Tv className="w-6 h-6 text-sky-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">
                        {game.home_team_name || 'TBD'} vs {game.away_team_name || 'TBD'}
                      </p>
                      <p className="text-sm text-gray-400">
                        {game.league_name} • {formatDate(game.scheduled_time)} at {formatTime(game.scheduled_time)}
                      </p>
                      <p className="text-xs text-yellow-400">
                        ⏰ Starts in {game.hours_until} hours
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => handleGeneratePromo(game, 'square')}
                      disabled={generatingPromo === game.id}
                      className="bg-sky-400 hover:bg-sky-500 text-gray-900"
                    >
                      <QrCode className="w-4 h-4 mr-1" />
                      {generatingPromo === game.id ? 'Generating...' : 'Square'}
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => handleGeneratePromo(game, 'story')}
                      disabled={generatingPromo === game.id}
                      variant="outline"
                      className="border-sky-400 text-sky-300"
                    >
                      <QrCode className="w-4 h-4 mr-1" />
                      Story
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No upcoming streams message */}
      {!loadingStreams && upcomingStreams.length === 0 && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardContent className="p-6 text-center">
            <Bell className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No streams scheduled within the next 24 hours.</p>
            <p className="text-gray-500 text-sm">Add games with video sources to get promo graphic notifications.</p>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-sky-200">{stats.sports || 0}</div>
            <div className="text-sm text-gray-400">Sports</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-sky-200">{stats.leagues || 0}</div>
            <div className="text-sm text-gray-400">Leagues</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-sky-200">{stats.teams || 0}</div>
            <div className="text-sm text-gray-400">Teams</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-sky-200">{stats.games || 0}</div>
            <div className="text-sm text-gray-400">Games</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-red-400">{stats.live_games || 0}</div>
            <div className="text-sm text-gray-400">Live Now</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-sky-200">{stats.fixtures || 0}</div>
            <div className="text-sm text-gray-400">Fixtures</div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-gray-800/50 border-sky-900/30">
        <CardHeader>
          <CardTitle className="text-white">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={handleSeed} 
            disabled={seeding}
            className="bg-sky-300 hover:bg-sky-400 text-gray-900"
          >
            {seeding ? 'Seeding...' : 'Seed Default Sports & Leagues'}
          </Button>
          <p className="text-sm text-gray-400">
            This will create the default sports (Soccer, Gaelic Football, Hurling/Camogie) and all their leagues.
          </p>
        </CardContent>
      </Card>

      {/* Sports & Leagues Overview */}
      <Card className="bg-gray-800/50 border-sky-900/30">
        <CardHeader>
          <CardTitle className="text-white">Sports & Leagues Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {sports.map(sport => (
              <div key={sport.id} className="border-b border-gray-700 pb-4 last:border-0">
                <h3 className="text-lg font-semibold text-white mb-2">{sport.icon} {sport.name}</h3>
                <div className="flex flex-wrap gap-2">
                  {leagues.filter(l => l.sport_id === sport.id).map(league => (
                    <Badge key={league.id} variant="outline" className="border-sky-400 text-sky-100">
                      {league.name}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Promo Graphic Modal */}
      <Dialog open={showPromoModal} onOpenChange={setShowPromoModal}>
        <DialogContent className="max-w-2xl bg-gray-900 border-sky-900/50">
          <DialogHeader>
            <DialogTitle className="text-white">Promotional Graphic</DialogTitle>
          </DialogHeader>
          {generatedPromo && selectedGame && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-gray-400 mb-2">
                  {selectedGame.home_team_name} vs {selectedGame.away_team_name}
                </p>
                <img 
                  src={`data:image/png;base64,${generatedPromo.base64}`}
                  alt="Promo graphic"
                  className={`mx-auto rounded-lg ${generatedPromo.format_type === 'story' ? 'max-h-96' : 'max-w-md'}`}
                />
              </div>
              <div className="flex justify-center gap-4">
                <Button onClick={shareToInstagram} className="bg-gradient-to-r from-purple-500 to-pink-500">
                  <Share2 className="w-4 h-4 mr-2" /> Share to Instagram
                </Button>
                <Button onClick={downloadPromo} variant="outline" className="border-gray-600">
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
              </div>
              <p className="text-center text-gray-500 text-sm">
                The graphic includes a QR code that links directly to your stream!
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin Partners Management

export { AdminDashboard };
